/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

const AudioRecordingWorklet = `
class AudioProcessingWorklet extends AudioWorkletProcessor {
  targetSampleRate = 16000;
  inputSampleRate = sampleRate;
  resampleRatio = sampleRate / this.targetSampleRate;
  leftover = new Float32Array(0);

  // send and clear buffer every 2048 samples, 
  // which at 16khz is about 8 times a second
  buffer = new Int16Array(2048);

  // current write index
  bufferWriteIndex = 0;

  constructor() {
    super();
    this.hasAudio = false;
  }

  /**
   * @param inputs Float32Array[][] [input#][channel#][sample#] so to access first inputs 1st channel inputs[0][0]
   * @param outputs Float32Array[][]
   */
  process(inputs) {
    if (inputs[0].length) {
      const channel0 = inputs[0][0];
      this.processChunk(channel0);
    }
    return true;
  }

  sendAndClearBuffer(){
    this.port.postMessage({
      event: "chunk",
      data: {
        int16arrayBuffer: this.buffer.slice(0, this.bufferWriteIndex).buffer,
      },
    });
    this.bufferWriteIndex = 0;
  }

  processChunk(float32Array) {
    if (this.inputSampleRate === this.targetSampleRate) {
      this.writeSamples(float32Array);
      return;
    }

    const input = new Float32Array(this.leftover.length + float32Array.length);
    input.set(this.leftover);
    input.set(float32Array, this.leftover.length);

    let offset = 0;
    const outputLength = Math.floor(input.length / this.resampleRatio);

    for (let i = 0; i < outputLength; i++) {
      const nextOffset = Math.min(
        input.length,
        Math.round((i + 1) * this.resampleRatio),
      );

      let sum = 0;
      let count = 0;

      for (let j = offset; j < nextOffset; j++) {
        sum += input[j];
        count++;
      }

      if (count > 0) {
        this.writeSample(sum / count);
      }

      offset = nextOffset;
    }

    this.leftover = input.slice(offset);
  }

  writeSamples(float32Array) {
    const l = float32Array.length;

    for (let i = 0; i < l; i++) {
      this.writeSample(float32Array[i]);
    }
  }

  writeSample(sample) {
    const clamped = Math.max(-1, Math.min(1, sample));
    const int16Value = clamped < 0
      ? clamped * 32768
      : clamped * 32767;

    this.buffer[this.bufferWriteIndex++] = int16Value;

    if (this.bufferWriteIndex >= this.buffer.length) {
      this.sendAndClearBuffer();
    }
  }
}
`;

export default AudioRecordingWorklet;
